import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchMailboxMessagesV2,
  importMailboxMessageV2,
  type MailboxMessageV2,
} from '../api/v2/mailbox';

/** A subject that could not be imported, paired with the reason it was rejected. */
interface RejectedImport {
  subject: string;
  reason: string;
}

/** The success banner names a single subject, or a count for a bulk run. */
type SuccessBanner =
  | { kind: 'single'; subject: string }
  | { kind: 'bulk'; count: number };

export default function MailboxIngestionV2() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  const [messages, setMessages] = useState<MailboxMessageV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [successBanner, setSuccessBanner] = useState<SuccessBanner | null>(null);
  const [rejected, setRejected] = useState<RejectedImport[]>([]);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMailboxMessagesV2();
      setMessages(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('mailbox.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  function removeRow(id: string) {
    setMessages((prev) => prev.filter((m) => m.exchange_message_id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function reasonFor(outcome: 'already_imported' | 'filtered_out'): string {
    return outcome === 'already_imported'
      ? t('mailboxV2.alreadyImported')
      : t('mailboxV2.filteredOut');
  }

  async function handleImport(msg: MailboxMessageV2) {
    if (!window.confirm(t('mailbox.confirmImport', { subject: msg.subject }))) return;
    const subject = msg.subject || t('mailbox.noSubject');
    setImportingId(msg.exchange_message_id);
    setSuccessBanner(null);
    setRejected([]);
    try {
      const outcome = await importMailboxMessageV2(msg.exchange_message_id);
      if (outcome === 'imported') {
        removeRow(msg.exchange_message_id);
        setSuccessBanner({ kind: 'single', subject });
      } else {
        setRejected([{ subject, reason: reasonFor(outcome) }]);
      }
    } catch (err) {
      setRejected([
        {
          subject,
          reason: err instanceof Error ? err.message : t('mailbox.importFailed'),
        },
      ]);
    } finally {
      setImportingId(null);
    }
  }

  async function handleBulkImport() {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(t('mailbox.confirmBulkImport', { count }))) return;

    setBulkImporting(true);
    setSuccessBanner(null);
    setRejected([]);

    const importedIds: string[] = [];
    const rejects: RejectedImport[] = [];

    // Sequential — one import request at a time, in selection order (spec §3).
    for (const id of selectedIds) {
      const msg = messages.find((m) => m.exchange_message_id === id);
      const subject = msg?.subject || t('mailbox.noSubject');
      try {
        const outcome = await importMailboxMessageV2(id);
        if (outcome === 'imported') importedIds.push(id);
        else rejects.push({ subject, reason: reasonFor(outcome) });
      } catch (err) {
        rejects.push({
          subject,
          reason: err instanceof Error ? err.message : t('mailbox.importFailed'),
        });
      }
    }

    const importedSet = new Set(importedIds);
    setMessages((prev) => prev.filter((m) => !importedSet.has(m.exchange_message_id)));
    setSelectedIds(new Set());
    setBulkImporting(false);

    if (importedIds.length > 0) {
      setSuccessBanner({ kind: 'bulk', count: importedIds.length });
    }
    setRejected(rejects);
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === messages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(messages.map((m) => m.exchange_message_id)));
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('mailboxV2.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('mailboxV2.subtitle')}</p>
        </div>
      </header>

      {/* Actions */}
      <section aria-label={t('mailbox.filters')} className="flex flex-wrap items-center gap-3">
        <button onClick={loadMessages} className="btn-secondary" aria-label={t('mailbox.refreshMailbox')}>
          {t('common.refresh')}
        </button>
        {selectedIds.size > 0 && (
          <button
            onClick={handleBulkImport}
            disabled={bulkImporting}
            className="btn-primary"
            aria-busy={bulkImporting}
          >
            {bulkImporting
              ? t('mailbox.importingMsg')
              : t('mailbox.importSelected', { count: selectedIds.size })}
          </button>
        )}
      </section>

      {/* Success banner */}
      {successBanner && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-700"
        >
          {successBanner.kind === 'single'
            ? t('mailboxV2.importQueued', { subject: successBanner.subject })
            : t('mailboxV2.importsQueued', { count: successBanner.count })}{' '}
          <Link to="/v2/tenders" className="font-semibold underline hover:text-green-900">
            {t('mailboxV2.viewTenders')}
          </Link>
        </div>
      )}

      {/* Rejected imports (409 / 422) */}
      {rejected.length > 0 && (
        <div
          role="alert"
          className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800"
        >
          <ul className="space-y-1">
            {rejected.map((r, i) => (
              <li key={`${r.subject}-${i}`}>
                <span className="font-medium">{r.subject}</span>
                {' — '}
                {r.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Error alert */}
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
          <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">
            &#x27F3;
          </span>
          {t('mailbox.loading')}
        </div>
      )}

      {/* Message list */}
      {!loading && !error && (
        <>
          {messages.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-500 text-sm">{t('mailbox.noMessages')}</p>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="data-table" aria-label={t('mailbox.messageTable')}>
                  <thead>
                    <tr>
                      <th scope="col" className="w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === messages.length && messages.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          aria-label={t('mailbox.selectAll')}
                        />
                      </th>
                      <th scope="col">{t('mailbox.subject')}</th>
                      <th scope="col">{t('mailbox.from')}</th>
                      <th scope="col">{t('mailbox.received')}</th>
                      <th scope="col">
                        <span className="sr-only">{t('common.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg) => (
                      <tr key={msg.exchange_message_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(msg.exchange_message_id)}
                            onChange={() => toggleSelect(msg.exchange_message_id)}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                            aria-label={t('mailbox.selectMsg', { subject: msg.subject })}
                          />
                        </td>
                        <td className="max-w-xs">
                          <span className="truncate block max-w-full text-gray-700">
                            {msg.subject || t('mailbox.noSubject')}
                          </span>
                        </td>
                        <td className="text-gray-600 text-sm">{msg.email}</td>
                        <td className="text-sm">
                          {msg.received_at ? (
                            <time dateTime={msg.received_at}>
                              {new Date(msg.received_at).toLocaleDateString(dateLocale, {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })}{' '}
                              <span className="text-gray-400">
                                {new Date(msg.received_at).toLocaleTimeString(dateLocale, {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </time>
                          ) : (
                            <span className="text-gray-400">{t('mailboxV2.noReceivedDate')}</span>
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => handleImport(msg)}
                            disabled={importingId === msg.exchange_message_id || bulkImporting}
                            className="text-xs text-brand-600 hover:text-brand-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                            aria-label={t('mailbox.importAsLabel', { subject: msg.subject })}
                          >
                            {importingId === msg.exchange_message_id
                              ? t('mailbox.importingMsg')
                              : t('mailbox.importAsTender')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
